import type { CampaignPayload, Catalog, GameCommand } from "../types";
import {
  campaignCapability,
  clearPendingCampaignCapability,
  commitCampaignCapabilityRotation,
  pendingCampaignCapability,
  recordCampaign,
  removeCampaignFromDevice,
  stageCampaignCapabilityRotation,
} from "./campaignStorage";

export class ApiError extends Error {
  code: string;
  status: number;

  constructor(message: string, code = "request_failed", status = 500) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

class AmbiguousSuccessBodyError extends Error {
  constructor(options?: ErrorOptions) {
    super("The server accepted the request but its response body was incomplete.", options);
    this.name = "AmbiguousSuccessBodyError";
  }
}

type RawCampaignPayload = Omit<CampaignPayload, "catalog"> & {
  catalog?: Catalog;
};

let cachedCatalog: Catalog | null = null;
let pendingCatalog: Promise<Catalog> | null = null;

function randomCapability(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

export function resetCatalogCacheForTests() {
  cachedCatalog = null;
  pendingCatalog = null;
}

async function request<T>(
  url: string,
  init?: RequestInit,
  capability?: { campaignId: string; accessKey?: string },
  options?: { retryAmbiguousNetworkOnce?: boolean },
): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("Accept", "application/json");
  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const campaignAccessKey = capability
    ? capability.accessKey ?? campaignCapability(capability.campaignId)
    : undefined;
  if (campaignAccessKey) {
    headers.set("X-Campaign-Key", campaignAccessKey);
  }
  const method = (init?.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS", "TRACE"].includes(method)) {
    const csrfToken = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith("csrftoken="))
      ?.slice("csrftoken=".length);
    if (csrfToken) {
      headers.set("X-CSRFToken", decodeURIComponent(csrfToken));
    }
  }
  const requestInit: RequestInit = {
    ...init,
    credentials: "same-origin",
    headers,
  };
  const attempt = async () => {
    const response = await fetch(url, requestInit);
    let body:
      | { error?: { code?: string; message?: string }; detail?: string }
      | T
      | null;
    try {
      body = (await response.json()) as typeof body;
    } catch (error) {
      // Any unreadable 2xx body is ambiguous: the authoritative write may
      // already be committed even when fetch ended cleanly with truncated JSON.
      if (response.ok) {
        throw new AmbiguousSuccessBodyError({
          cause: error instanceof Error ? error : undefined,
        });
      }
      body = null;
    }
    if (!response.ok) {
      const error = body as { error?: { code?: string; message?: string }; detail?: string } | null;
      throw new ApiError(
        error?.error?.message ?? error?.detail ?? "The bunker could not be reached.",
        error?.error?.code,
        response.status,
      );
    }
    return body as T;
  };

  try {
    return await attempt();
  } catch (error) {
    const ambiguous =
      error instanceof TypeError || error instanceof AmbiguousSuccessBodyError;
    if (!options?.retryAmbiguousNetworkOnce || !ambiguous) {
      if (error instanceof AmbiguousSuccessBodyError) {
        throw new ApiError(
          "The server response was incomplete. Refresh the campaign before issuing another order.",
          "ambiguous_success_response",
        );
      }
      throw error;
    }
    try {
      return await attempt();
    } catch (retryError) {
      if (retryError instanceof AmbiguousSuccessBodyError) {
        throw new ApiError(
          "The server response remained incomplete after a safe retry. Refresh the campaign before issuing another order.",
          "ambiguous_success_response",
        );
      }
      throw retryError;
    }
  }
}

function catalogMatchesPayload(catalog: Catalog, payload: RawCampaignPayload) {
  const contract = payload.contract;
  if (!contract) return true;
  return (
    catalog.api_version === contract.api_version &&
    catalog.ruleset_version === contract.ruleset_version &&
    catalog.catalog_revision === contract.catalog_revision &&
    catalog.state_schema_version === contract.state_schema_version
  );
}

async function loadCatalog(forceReload = false): Promise<Catalog> {
  if (!forceReload && cachedCatalog) return cachedCatalog;
  if (!forceReload && pendingCatalog) return pendingCatalog;
  const operation = request<Catalog>("/api/catalog/", {
    cache: forceReload ? "reload" : "default",
  }).then((catalog) => {
    cachedCatalog = catalog;
    return catalog;
  });
  if (forceReload) return operation;
  pendingCatalog = operation;
  try {
    return await operation;
  } finally {
    pendingCatalog = null;
  }
}

async function attachCatalog(payload: RawCampaignPayload): Promise<CampaignPayload> {
  if (payload.catalog && catalogMatchesPayload(payload.catalog, payload)) {
    cachedCatalog = payload.catalog;
    return payload as CampaignPayload;
  }
  // Contract-less payloads are only used by legacy test fixtures. Every live
  // campaign response carries a complete immutable contract tuple.
  if (!payload.contract) return payload as CampaignPayload;

  let catalog = await loadCatalog();
  if (!catalogMatchesPayload(catalog, payload)) {
    catalog = await loadCatalog(true);
  }
  if (!catalogMatchesPayload(catalog, payload)) {
    throw new ApiError(
      "The campaign and game catalog came from different releases. Refresh before opening this save.",
      "catalog_contract_mismatch",
      409,
    );
  }
  return { ...payload, catalog };
}

async function probeCampaign(id: string, accessKey: string): Promise<RawCampaignPayload> {
  return request<RawCampaignPayload>(
    `/api/campaigns/${id}/`,
    undefined,
    { campaignId: id, accessKey },
  );
}

export async function createCampaign(name: string, seed?: number): Promise<CampaignPayload> {
  const campaignId = crypto.randomUUID();
  const accessKey = randomCapability();
  const envelope = {
    name,
    campaign_id: campaignId,
    access_key: accessKey,
    ...(seed ? { debug_seed: seed } : {}),
  };
  // The browser chooses both identifiers and persists them before the first
  // write. A same-envelope retry therefore returns the same campaign/key.
  recordCampaign({ id: campaignId, name, access_key: accessKey }, { activate: true });
  let payload: RawCampaignPayload;
  try {
    payload = await request<RawCampaignPayload>(
      "/api/campaigns/",
      { method: "POST", body: JSON.stringify(envelope) },
      undefined,
      { retryAmbiguousNetworkOnce: true },
    );
  } catch (error) {
    // If both responses were lost after commit, prove ownership with the
    // client-generated capability instead of creating a second campaign.
    try {
      payload = await probeCampaign(campaignId, accessKey);
      payload.campaign.access_key = accessKey;
    } catch (probeError) {
      // A definitive missing probe proves neither create attempt committed, so
      // do not leave a dead provisional campaign in the local registry.
      if (
        (probeError instanceof ApiError && probeError.status === 404) ||
        (error instanceof ApiError && error.status < 500)
      ) {
        removeCampaignFromDevice(campaignId);
      }
      throw error instanceof Error ? error : probeError;
    }
  }
  // Anonymous capabilities are returned once. Persist before any secondary
  // catalog request so a cache/network failure cannot orphan the new save.
  if (payload.campaign?.access_key) {
    recordCampaign(payload.campaign, { activate: true });
  }
  return attachCatalog(payload);
}

export async function getCampaign(
  id: string,
  signal?: AbortSignal,
  accessKey?: string,
): Promise<CampaignPayload> {
  let payload: RawCampaignPayload;
  try {
    payload = await request<RawCampaignPayload>(
      `/api/campaigns/${id}/`,
      { signal },
      { campaignId: id, accessKey },
    );
  } catch (error) {
    const pending = !accessKey ? pendingCampaignCapability(id) : undefined;
    if (!(error instanceof ApiError) || error.status !== 404 || !pending) throw error;
    try {
      payload = await probeCampaign(id, pending);
      commitCampaignCapabilityRotation(id);
    } catch (pendingError) {
      if (pendingError instanceof ApiError && pendingError.status === 404) {
        clearPendingCampaignCapability(id);
      }
      throw error;
    }
  }
  return attachCatalog(payload);
}

export async function rotateCampaignAccess(id: string): Promise<CampaignPayload> {
  const currentAccessKey = campaignCapability(id);
  if (!currentAccessKey) {
    throw new ApiError("This anonymous save has no recovery capability.", "capability_missing", 400);
  }
  const replacementAccessKey = randomCapability();
  stageCampaignCapabilityRotation(id, replacementAccessKey);
  const submit = () => request<RawCampaignPayload>(
    `/api/campaigns/${id}/access/rotate/`,
    {
      method: "POST",
      body: JSON.stringify({ replacement_access_key: replacementAccessKey }),
    },
    { campaignId: id, accessKey: currentAccessKey },
  );
  const probeReplacement = async () => {
    const recovered = await probeCampaign(id, replacementAccessKey);
    recovered.campaign.access_key = replacementAccessKey;
    commitCampaignCapabilityRotation(id);
    return recovered;
  };

  let payload: RawCampaignPayload;
  try {
    payload = await submit();
  } catch (firstError) {
    try {
      payload = await probeReplacement();
    } catch (probeError) {
      if (!(probeError instanceof ApiError) || probeError.status !== 404) {
        throw firstError;
      }
      try {
        payload = await submit();
      } catch (retryError) {
        try {
          payload = await probeReplacement();
        } catch (finalProbeError) {
          if (retryError instanceof ApiError && retryError.status < 500) {
            clearPendingCampaignCapability(id);
          }
          throw retryError instanceof Error ? retryError : finalProbeError;
        }
      }
    }
  }
  // Rotation revokes the old bearer immediately. Store the replacement before
  // catalog validation; keep whichever campaign was already active.
  if (payload.campaign?.access_key) {
    recordCampaign(payload.campaign, { activate: false });
  } else if (payload.campaign) {
    payload.campaign.access_key = replacementAccessKey;
    commitCampaignCapabilityRotation(id);
  }
  return attachCatalog(payload);
}

export async function sendCommand(
  payload: CampaignPayload,
  command: GameCommand,
  idempotencyKey: string = crypto.randomUUID(),
): Promise<CampaignPayload> {
  const response = await request<RawCampaignPayload>(
    `/api/campaigns/${payload.campaign.id}/commands/`,
    {
      method: "POST",
      body: JSON.stringify({
        expected_revision: payload.campaign.revision,
        idempotency_key: idempotencyKey,
        command,
      }),
    },
    { campaignId: payload.campaign.id },
    { retryAmbiguousNetworkOnce: true },
  );
  return attachCatalog(response);
}

export async function decideCrisis(
  payload: CampaignPayload,
  decision: "accept" | "take_command",
  idempotencyKey: string = crypto.randomUUID(),
): Promise<CampaignPayload> {
  const response = await request<RawCampaignPayload>(
    `/api/campaigns/${payload.campaign.id}/crisis/`,
    {
      method: "POST",
      body: JSON.stringify({
        expected_revision: payload.campaign.revision,
        decision,
        idempotency_key: idempotencyKey,
      }),
    },
    { campaignId: payload.campaign.id },
    { retryAmbiguousNetworkOnce: true },
  );
  return attachCatalog(response);
}
